import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Snackbar,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTime } from 'luxon';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { createCrop, deleteCrop, getAllCrops } from '../api/performance.api';
import { ExclusionZone, PerformanceDataUpload, RawDataPointEnhanced } from '../community.types';
import { formatNumber, formatTimestampSeconds } from '../community.func';
import CustomTooltipRawGpsData from '../../../components/Charting/CustomTooltipRawGpsData';

interface ExclusionZoneDialogProps {
    open: boolean;
    onClose: (changed: boolean) => void;
    upload: PerformanceDataUpload;
    sampledData: RawDataPointEnhanced[];
    rawData: RawDataPointEnhanced[];
    distanceUnit: 'yd' | 'ft' | 'm';
}

const ExclusionZoneDialog: React.FC<ExclusionZoneDialogProps> = ({
    open,
    onClose,
    upload,
    sampledData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rawData,
    distanceUnit,
}) => {
    const [zones, setZones] = useState<ExclusionZone[]>([]);
    const [hasChanged, setHasChanged] = useState(false);
    const [dragStart, setDragStart] = useState<number | null>(null);
    const [dragEnd, setDragEnd] = useState<number | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState<string | null>(null);
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);

    // Load exclusion zones when dialog opens
    useEffect(() => {
        if (open) {
            setLoading(true);
            setHasChanged(false);
            getAllCrops(upload.individual.uuid, upload.uuid)
                .then((response) => {
                    const exclusions = response.data.filter(
                        (c) =>
                            c.label.startsWith('__exclusion__') || c.label.startsWith('__trim__'),
                    );
                    setZones(exclusions);
                })
                .catch(() => {
                    setMessage('Error loading exclusion zones');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open, upload]);

    const handleMouseDown = useCallback(
        (e: any) => {
            if (e && e.activeLabel != null && !isCreating) {
                setDragStart(+e.activeLabel);
                setDragEnd(null);
            }
        },
        [isCreating],
    );

    const handleMouseMove = useCallback(
        (e: any) => {
            if (e && e.activeLabel != null && dragStart !== null && !isCreating) {
                setDragEnd(+e.activeLabel);
            }
        },
        [dragStart, isCreating],
    );

    const handleMouseUp = useCallback(() => {
        if (dragStart === null || dragEnd === null || isCreating) {
            setDragStart(null);
            setDragEnd(null);
            return;
        }

        const start = Math.min(dragStart, dragEnd);
        const end = Math.max(dragStart, dragEnd);

        // Minimum drag distance: 500ms worth of timestamp
        if (end - start < 500) {
            setDragStart(null);
            setDragEnd(null);
            return;
        }

        setIsCreating(true);
        createCrop(upload.individual.uuid, upload.uuid, {
            label: '__exclusion__',
            start_timestamp: start,
            end_timestamp: end,
        })
            .then((response) => {
                setZones((prev) => [...prev, response.data]);
                setHasChanged(true);
                setMessage('Exclusion zone created');
            })
            .catch(() => {
                setMessage('Error creating exclusion zone');
            })
            .finally(() => {
                setDragStart(null);
                setDragEnd(null);
                setIsCreating(false);
            });
    }, [dragStart, dragEnd, isCreating, upload]);

    const handleDelete = useCallback(
        (zoneId: string) => {
            setIsDeleting(zoneId);
            deleteCrop(upload.individual.uuid, upload.uuid, zoneId)
                .then(() => {
                    setZones((prev) => prev.filter((z) => z.uuid !== zoneId));
                    setHasChanged(true);
                    setMessage('Exclusion zone deleted');
                })
                .catch(() => {
                    setMessage('Error deleting exclusion zone');
                })
                .finally(() => {
                    setIsDeleting(null);
                });
        },
        [upload],
    );

    const handleClose = useCallback(() => {
        onClose(hasChanged);
        setDragStart(null);
        setDragEnd(null);
    }, [onClose, hasChanged]);

    const formatZoneTime = (timestamp: number) => {
        return DateTime.fromMillis(timestamp).toFormat('h:mm:ss a');
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
                <DialogTitle>Manage Exclusion Zones</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, minHeight: 450 }}>
                        {/* Left panel - Chart */}
                        <Box sx={{ flex: '0 0 70%', position: 'relative' }}>
                            {loading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: 400,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <ResponsiveContainer width="100%" height={400}>
                                    <AreaChart
                                        data={sampledData}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 0,
                                            bottom: 0,
                                        }}
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            domain={['dataMin', 'dataMax']}
                                            type="number"
                                            dataKey="Timestamp"
                                            angle={-60}
                                            tickFormatter={formatTimestampSeconds}
                                            textAnchor="end"
                                            height={100}
                                            style={{
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                        <YAxis
                                            name={'Velocity (mph)'}
                                            domain={[0, 'dataMax']}
                                            tickFormatter={formatNumber}
                                        />
                                        <Tooltip
                                            content={(props) => (
                                                <CustomTooltipRawGpsData
                                                    {...props}
                                                    hideDurationPrefix={true}
                                                    distanceUnit={distanceUnit}
                                                />
                                            )}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="Velocity (mph)"
                                            stroke="#8884d8"
                                            fill="#8884d8"
                                        />
                                        {/* Existing exclusion zones */}
                                        {zones.map((ez) => (
                                            <ReferenceArea
                                                key={`exclusion-${ez.uuid}`}
                                                x1={ez.start_timestamp}
                                                x2={ez.end_timestamp}
                                                fill="rgba(128, 128, 128, 0.35)"
                                                strokeOpacity={0}
                                                ifOverflow="hidden"
                                            />
                                        ))}
                                        {/* Currently being created zone */}
                                        {dragStart !== null && dragEnd !== null && (
                                            <ReferenceArea
                                                x1={Math.min(dragStart, dragEnd)}
                                                x2={Math.max(dragStart, dragEnd)}
                                                fill="rgba(128, 128, 128, 0.55)"
                                                strokeOpacity={0}
                                                ifOverflow="hidden"
                                            />
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                            {isCreating && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>

                        {/* Right panel - List */}
                        <Box
                            sx={{
                                flex: '0 0 30%',
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                pl: 2,
                                overflow: 'auto',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Exclusion Zones
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 2 }}
                            >
                                Drag on the chart to create a new exclusion zone
                            </Typography>
                            {zones.length === 0 && !loading && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'center', mt: 4 }}
                                >
                                    No exclusion zones
                                </Typography>
                            )}
                            <List dense>
                                {zones.map((zone) => (
                                    <ListItem key={zone.uuid} sx={{ pr: 6 }}>
                                        <ListItemText
                                            primary={`${formatZoneTime(
                                                zone.start_timestamp,
                                            )} to ${formatZoneTime(zone.end_timestamp)}`}
                                            secondary={`${(
                                                (zone.end_timestamp - zone.start_timestamp) /
                                                1000
                                            ).toFixed(1)}s`}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleDelete(zone.uuid)}
                                                disabled={isDeleting === zone.uuid}
                                                size="small"
                                            >
                                                {isDeleting === zone.uuid ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <DeleteIcon />
                                                )}
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={!!message}
                autoHideDuration={1500}
                onClose={() => setMessage('')}
                message={message}
            />
        </>
    );
};

export default ExclusionZoneDialog;
